import { useRef, useEffect, useState, useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import type { AppState, CompletionResult } from '@/lib/types';

export const STATE_KEY = ['state'] as const;

/** The whole app state, or null when this device isn't signed in. */
export function useAppState() {
  return useQuery({
    queryKey: STATE_KEY,
    queryFn: async () => {
      try {
        return await api<AppState>('GET', '/state');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 30_000,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500 && err.status > 0) && count < 2,
  });
}

type Returns = AppState | { result: CompletionResult; state: AppState } | unknown;

/**
 * A server action that returns fresh state (or { result, state }). Shows errors
 * as toasts, stores the new state, and exposes isPending for spinners.
 */
export function useAction<V = void>(fn: (vars: V) => Promise<Returns>, opts: { onSuccess?: (data: any) => void; silent?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data: any) => {
      if (data && typeof data === 'object' && 'me' in data) qc.setQueryData(STATE_KEY, data);
      else if (data && typeof data === 'object' && 'state' in data) qc.setQueryData(STATE_KEY, data.state);
      opts.onSuccess?.(data);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) {
        qc.setQueryData(STATE_KEY, null);
        return;
      }
      if (!opts.silent) toast.error((err as Error).message);
    },
  });
}

// ---------- optimistic settings ----------

/** Settings saves still on their way; live sync waits for them so it can't show a stale value. */
let savesInFlight = 0;

/**
 * Optimistic settings. A change shows up at once; saves go out one at a time.
 * Anything changed while a save is on its way is merged and sent next, so
 * rapid taps (a weekday tapped 1, 2, 0) end in one save of the final value,
 * and saves can never arrive out of order. If a save fails, the app goes back
 * to what the server has and says why.
 */
function useLatestPatch<P extends Record<string, unknown>>(path: string, apply: (s: AppState, p: P) => AppState) {
  const qc = useQueryClient();
  const queue = useRef<{ inflight: boolean; queued: P | null; waiters: { resolve: () => void; reject: (e: unknown) => void }[] }>({
    inflight: false,
    queued: null,
    waiters: [],
  });
  const [pending, setPending] = useState(false);

  const flush = async () => {
    const q = queue.current;
    if (q.inflight || !q.queued) return;
    const body = q.queued;
    const waiters = q.waiters;
    q.queued = null;
    q.waiters = [];
    q.inflight = true;
    savesInFlight++;
    setPending(true);
    try {
      const state = await api<AppState>('PATCH', path, body);
      // Newer changes are waiting: keep showing those instead of this answer.
      if (!q.queued) qc.setQueryData(STATE_KEY, state);
      waiters.forEach((w) => w.resolve());
    } catch (err) {
      // Drop what was waiting too, and go back to the server's truth.
      const all = [...waiters, ...q.waiters];
      q.queued = null;
      q.waiters = [];
      all.forEach((w) => w.reject(err));
      if (err instanceof ApiError && err.status === 401) qc.setQueryData(STATE_KEY, null);
      else {
        toast.error((err as Error).message);
        void qc.invalidateQueries({ queryKey: STATE_KEY });
      }
    } finally {
      q.inflight = false;
      savesInFlight--;
      if (q.queued) void flush();
      else setPending(false);
    }
  };

  const save = (patch: P) => {
    void qc.cancelQueries({ queryKey: STATE_KEY }); // a refetch landing now would undo the change on screen
    qc.setQueryData<AppState | null>(STATE_KEY, (s) => (s ? apply(s, patch) : s));
    const q = queue.current;
    q.queued = { ...(q.queued ?? {}), ...patch } as P;
    const done = new Promise<void>((resolve, reject) => q.waiters.push({ resolve, reject }));
    void flush();
    return done;
  };
  return { save, pending };
}

type MePatch = Partial<Pick<AppState['me'], 'name' | 'avatar' | 'locale' | 'tz' | 'reminderTime' | 'budgetMinutes' | 'notifyDaily' | 'notifyStreak' | 'notifyTeam' | 'notifyRecap' | 'schedule'>>;
type HouseholdPatch = Partial<Pick<AppState['household'], 'name' | 'plannedMembers'>>;

/** Change your own settings, optimistically. */
export const useMePatch = () => useLatestPatch<MePatch>('/me', (s, p) => ({ ...s, me: { ...s.me, ...p } }));
/** Change the household's name or size, optimistically. */
export const useHouseholdPatch = () => useLatestPatch<HouseholdPatch>('/household', (s, p) => ({ ...s, household: { ...s.household, ...p } }));

/**
 * Live sync: the server says when something in the household changed (a
 * housemate finished a chore, someone edited the list) and we refetch.
 */
export function useLiveSync(enabled: boolean) {
  const qc = useQueryClient();
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!enabled || !('EventSource' in window)) return;
    const source = new EventSource('/api/events');
    const refresh = () => {
      clearTimeout(timer.current);
      timer.current = window.setTimeout(function run() {
        // Our own settings save is on its way: its answer is fresher than a refetch would be.
        if (savesInFlight > 0) timer.current = window.setTimeout(run, 300);
        else void qc.invalidateQueries({ queryKey: STATE_KEY });
      }, 250);
    };
    source.addEventListener('change', refresh);
    // After a reconnect, catch up on anything we missed.
    let first = true;
    source.addEventListener('ready', () => {
      if (!first) refresh();
      first = false;
    });
    return () => {
      clearTimeout(timer.current);
      source.close();
    };
  }, [enabled, qc]);
}

const subscribeOnline = (cb: () => void) => {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
};

/** Whether the browser is online; refetches the state when the connection comes back. */
export function useOnline() {
  const qc = useQueryClient();
  const online = useSyncExternalStore(subscribeOnline, () => navigator.onLine);
  const wasOffline = useRef(!online);
  useEffect(() => {
    if (online && wasOffline.current) qc.invalidateQueries({ queryKey: STATE_KEY });
    wasOffline.current = !online;
  }, [online, qc]);
  return online;
}
